import type { ReactNode } from 'react';

/**
 * Slim iPhone frame for the hero's right pane — bezel, status bar, dynamic
 * island, home indicator. Simplified port of the handoff's prototype scaffold
 * (ios-frame.jsx; not shipped): the device is authored at 402×874 (the iOS
 * spec space every screen-content pixel value in the handoff uses) and scaled
 * 0.659 into a 265×576 slot — an exact fit that keeps the handoff's paddings
 * 1:1. The scale transform is static; the hero's pop animation lives on the
 * parent wrapper and composes with it.
 *
 * Device-chrome hexes (#F2F2F7, black, neutral rgba) are part of the mock
 * artifact, like the acme-fiction screens inside — deliberately not tokens.
 * Parents mount this inside an aria-hidden container.
 */
export default function PhoneFrame({ children }: { children: ReactNode }) {
  return (
    <div className="h-[576px] w-[265px]">
      <div className="h-[874px] w-[402px] origin-top-left scale-[0.659]">
        <div
          className="relative h-full w-full overflow-hidden rounded-[48px] bg-[#F2F2F7] antialiased shadow-[0_40px_80px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.12)]"
          style={{ fontFamily: '-apple-system, system-ui, sans-serif' }}
        >
          <div className="absolute left-1/2 top-[11px] z-50 h-[37px] w-[126px] -translate-x-1/2 rounded-3xl bg-black" />
          {/* Two flex-1 halves with the spec's 154px island gap between them —
              time centers in the left segment, radios in the right. */}
          <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-center gap-[154px] px-6 pb-[19px] pt-[21px]">
            <div className="flex h-[22px] flex-1 items-center justify-center pt-px">
              <span className="text-[17px] font-[590] leading-[22px] text-black">9:41</span>
            </div>
            <div className="flex h-[22px] flex-1 items-center justify-center gap-[7px] pr-px pt-px">
              <svg width="19" height="12" viewBox="0 0 19 12">
                <rect x="0" y="7.5" width="3.2" height="4.5" rx="0.7" fill="#000" />
                <rect x="4.8" y="5" width="3.2" height="7" rx="0.7" fill="#000" />
                <rect x="9.6" y="2.5" width="3.2" height="9.5" rx="0.7" fill="#000" />
                <rect x="14.4" y="0" width="3.2" height="12" rx="0.7" fill="#000" />
              </svg>
              <svg width="17" height="12" viewBox="0 0 17 12">
                <path
                  d="M8.5 3.2C10.8 3.2 12.9 4.1 14.4 5.6L15.5 4.5C13.7 2.7 11.2 1.5 8.5 1.5C5.8 1.5 3.3 2.7 1.5 4.5L2.6 5.6C4.1 4.1 6.2 3.2 8.5 3.2Z"
                  fill="#000"
                />
                <path
                  d="M8.5 6.8C9.9 6.8 11.1 7.3 12 8.2L13.1 7.1C11.8 5.9 10.2 5.1 8.5 5.1C6.8 5.1 5.2 5.9 3.9 7.1L5 8.2C5.9 7.3 7.1 6.8 8.5 6.8Z"
                  fill="#000"
                />
                <circle cx="8.5" cy="10.5" r="1.5" fill="#000" />
              </svg>
              <svg width="27" height="13" viewBox="0 0 27 13">
                <rect
                  x="0.5"
                  y="0.5"
                  width="23"
                  height="12"
                  rx="3.5"
                  stroke="#000"
                  strokeOpacity="0.35"
                  fill="none"
                />
                <rect x="2" y="2" width="20" height="9" rx="2" fill="#000" />
                <path
                  d="M25 4.5V8.5C25.8 8.2 26.5 7.2 26.5 6.5C26.5 5.8 25.8 4.8 25 4.5Z"
                  fill="#000"
                  fillOpacity="0.4"
                />
              </svg>
            </div>
          </div>
          <div className="relative h-full">{children}</div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[60] flex h-[34px] items-end justify-center pb-2">
            <div className="h-[5px] w-[139px] rounded-full bg-black/25" />
          </div>
        </div>
      </div>
    </div>
  );
}
